/**
 * NewsroomReaderRepository — Profiteia's own approved articles, for readers.
 *
 * Endpoints (backend `api/endpoints/v2/news/newsroom_public.py`, public):
 *   GET /public/news?language=&limit=&offset=  → { articles: [...] }   (newest first, each `free`)
 *   GET /public/news/{slug}?language=          → one article, charts with their grant; `free` and
 *                                                `paywalled` (then without its body)
 *
 * Uses the anonymous `PublicClient` on every surface, the portal included: a refused request
 * must never bounce anyone to /login. A signed-in reader's access token goes along so the
 * backend can open a subscriber-only article; an expired one reads as anonymous, never a 401.
 */
import { getApiBaseUrl } from '@/app/runtime';
import PublicClient from '@/repositories/clients/PublicClient';
import normalizeBaseDomain from '@/repositories/clients/normalizeBaseDomain';

const resource = '/public/news';

// Articles already seen in a list, by slug: opening one shows its headline, lead and photo at
// once while the full article loads. Per page load only; the API stays the source of truth.
const seen = new Map();

// Read straight from session storage (where the shared `Client` reads it too) instead of the
// session store, so this public module pulls in no Pinia stores.
function readerHeaders() {
  let accessToken = null;
  try {
    accessToken = window.sessionStorage.getItem('accessToken');
  } catch {
    // No storage (private mode, server render): an anonymous reader.
  }
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

export default {
  /** @param {{ language?: 'nl'|'en', limit?: number, offset?: number }} [params] */
  async list({ language = 'nl', limit = 20, offset = 0 } = {}) {
    const response = await PublicClient.get(resource, { params: { language, limit, offset }, headers: readerHeaders() });
    for (const article of response?.data?.articles || []) seen.set(`${language}:${article.slug}`, article);
    return response;
  },

  externalFeed({ language = 'nl', limit = 30, offset = 0 } = {}) {
    return PublicClient.get(`${resource}/feed`, { params: { language, limit, offset } });
  },

  /** A list item for `slug` this page already loaded (card fields only), or null. */
  peek(slug, language = 'nl') {
    return seen.get(`${language}:${slug}`) || null;
  },

  /**
   * @param {string} slug @param {'nl'|'en'} [language]
   * @param {{ share?: string|null, unlock?: string|null }} [access] a share link (`?share=`) opens
   *   the article in full; `unlock` is the key its e-mail gate returned (ShareLinkRepository).
   */
  get(slug, language = 'nl', { share = null, unlock = null } = {}) {
    return PublicClient.get(`${resource}/${encodeURIComponent(slug)}`, {
      params: { language, ...(share ? { share } : {}), ...(unlock ? { unlock } : {}) },
      headers: readerHeaders(),
    });
  },

  /** Absolute URL for an API path the reader API returns (`image_url`). */
  apiUrl(path) {
    return path ? `${normalizeBaseDomain(getApiBaseUrl())}${path}` : null;
  },
};
